import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Github, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/lib/i18nContext";

export default function SiteFooter({ profile }) {
  const { t, tl, pathWithLang } = useI18n();
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative border-t hairline mt-24 bg-[var(--bg-1)]">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 pb-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Link to={pathWithLang("/")} aria-label="Home" className="inline-flex items-baseline gap-0.5">
            <span className="font-display text-2xl tracking-tight text-[var(--text-0)]">Karim Benna</span>
            <span className="w-2 h-2 rounded-full bg-[var(--gold)] ml-1 self-end mb-2" aria-hidden="true" />
          </Link>
          <p className="text-[var(--text-1)] mt-6 max-w-md leading-relaxed text-[15px]">
            {t("footer.tagline")}
          </p>
          <div className="flex gap-3 mt-7">
            <Link to={pathWithLang("/kontakt")} data-testid="footer-book-btn" className="btn-primary text-xs">
              {t("common.bookCall")} <ArrowUpRight size={14} />
            </Link>
            <Link to={pathWithLang("/kontakt")} data-testid="footer-contact-btn" className="btn-ghost text-xs">
              {t("common.sendMessage")}
            </Link>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-[0.18em] text-[var(--gold)] font-medium mb-5">
            {t("footer.navHeading")}
          </div>
          <ul className="space-y-3 text-[15px]">
            <li><Link className="link-gold" to={pathWithLang("/")}>{t("nav.home")}</Link></li>
            <li><Link className="link-gold" to={pathWithLang("/ueber-mich")}>{t("nav.about")}</Link></li>
            <li><Link className="link-gold" to={pathWithLang("/dienste")}>{t("nav.services")}</Link></li>
            <li><Link className="link-gold" to={pathWithLang("/erfahrung")}>{t("nav.experience")}</Link></li>
            <li><Link className="link-gold" to={pathWithLang("/blog")}>{t("nav.blog")}</Link></li>
            <li><Link className="link-gold" to={pathWithLang("/kontakt")}>{t("nav.contact")}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.18em] text-[var(--gold)] font-medium mb-5">
            {t("footer.contactHeading")}
          </div>
          <ul className="space-y-4 text-[15px] text-[var(--text-1)]">
            <li className="flex items-center gap-3">
              <Phone size={15} className="text-[var(--gold)]" />
              <a className="link-gold" href={`tel:${(profile?.phone || "").replace(/\s/g, "")}`}>
                {profile?.phone || "+49 176 62675596"}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={15} className="text-[var(--gold)]" />
              <a className="link-gold" href={`mailto:${profile?.email}`}>
                {profile?.email || "contact@karimbenna-itconsult.com"}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <MapPin size={15} className="text-[var(--gold)]" />
              <span>{tl(profile?.location) || "Deutschland · Portugal"}</span>
            </li>
          </ul>
          {profile?.cv_pdf_de && (
            <a
              href={profile.cv_pdf_de}
              download
              target="_blank"
              rel="noreferrer"
              data-testid="footer-cv-btn"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-2)] hover:text-[var(--gold)] mt-5 transition-colors"
            >
              {t("footer.cvLink")}
            </a>
          )}
          {(profile?.social?.linkedin || profile?.social?.github) && (
            <div className="flex gap-2 mt-6">
              {profile?.social?.linkedin && (
                <a href={profile.social.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 flex items-center justify-center border hairline rounded-full hover:border-[var(--gold)] hover:text-[var(--gold)] text-[var(--text-1)]" aria-label="LinkedIn">
                  <Linkedin size={14} />
                </a>
              )}
              {profile?.social?.github && (
                <a href={profile.social.github} target="_blank" rel="noreferrer" className="w-10 h-10 flex items-center justify-center border hairline rounded-full hover:border-[var(--gold)] hover:text-[var(--gold)] text-[var(--text-1)]" aria-label="GitHub">
                  <Github size={14} />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-[var(--text-2)]">
          <div>{t("footer.copyright", { year })}</div>
          <div className="flex gap-6">
            <Link to={pathWithLang("/impressum")} className="hover:text-[var(--gold)]">{t("footer.imprint")}</Link>
            <Link to={pathWithLang("/datenschutz")} className="hover:text-[var(--gold)]">{t("footer.privacy")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
